import { t } from '@lingui/macro'
import { AspenSchedulerService } from '@om1/aspen-api/codegen/client'
import { AppointmentDTO } from '@om1/aspen-api/codegen/client/models/AppointmentDTO'
import { Alert, DatePicker, Input, Modal, Spin } from 'antd'
import dayjs, { Dayjs } from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import { useCallback, useEffect, useState } from 'react'
import { appointmentZone, formatAppointmentSlot } from '../utils/appointment'

dayjs.extend(utc)
dayjs.extend(timezone)

/** A dayjs format pattern, not display copy — hoisted out of JSX so the
 * lingui lint rule does not read it as an untranslated string. */
const SLOT_TIME_FORMAT = 'h:mm A'

/**
 * Reschedule a candidate's appointment onto a real, available slot (APP-1221).
 *
 * ## Why this offers slots rather than a free datetime input
 *
 * A plain datetime field would let a coordinator pick a time Cal.com then
 * rejects — a 409 they can do nothing about except guess again. Worse, some
 * guesses would land on a slot that *is* free but that no Patient Manager is
 * rostered for, which is exactly the class of mistake the 4pm ET cutoff exists
 * to prevent. So this asks `GET /aspen/scheduler/slots` for the chosen day and
 * offers only what comes back.
 *
 * The backend enforces the cutoff again on the write path regardless — this is
 * a usability layer, not the guard.
 *
 * ## Times are shown in the PATIENT's zone
 *
 * The coordinator is arranging a call *for the patient*, so the patient's zone
 * is the one that matters, and it is labelled. Rendering in the coordinator's
 * own zone is how the current Eastern bug went unnoticed for so long.
 */

export interface RescheduleAppointmentModalProps {
    open: boolean
    candidateId: string
    candidateName: string
    appointment: AppointmentDTO
    candidateTimezone?: string | null
    /** Study slug — the Auth0 organization name the coordinator is scoped to. */
    study: string
    language?: 'en' | 'es' | null
    onClose: () => void
    /** Resolves true when the reschedule succeeded, so the caller can refetch. */
    onConfirm: (slotStartIso: string, reason: string) => Promise<boolean>
    isSubmitting?: boolean
}

const RescheduleAppointmentModal = ({
    open,
    candidateId,
    candidateName,
    appointment,
    candidateTimezone,
    study,
    language,
    onClose,
    onConfirm,
    isSubmitting = false
}: RescheduleAppointmentModalProps) => {
    const zone = appointmentZone(appointment, candidateTimezone)
    const [date, setDate] = useState<Dayjs | null>(null)
    const [slots, setSlots] = useState<string[]>([])
    const [selected, setSelected] = useState<string>('')
    const [reason, setReason] = useState('')
    const [loading, setLoading] = useState(false)
    const [failed, setFailed] = useState(false)

    const dayKey = date ? date.format('YYYY-MM-DD') : ''

    useEffect(() => {
        // Reset everything when the modal closes, so reopening for a different
        // candidate never shows the previous one's slots.
        if (!open) {
            setDate(null)
            setSlots([])
            setSelected('')
            setReason('')
            setFailed(false)
        }
    }, [open])

    useEffect(() => {
        if (!open || !dayKey || !study) {
            setSlots([])
            return
        }
        let current = true
        setLoading(true)
        setFailed(false)
        const request = AspenSchedulerService.getSlotsSchedulerSlotsGet({
            study,
            start: dayKey,
            end: dayKey,
            language: language ?? 'en',
            timeZone: zone
        })
        request
            .then((response) => {
                if (!current) return
                // The API groups by date key in the requested zone, and its own
                // contract warns a key can straddle two days in another zone —
                // so filter on each slot's instant rather than trusting the key.
                const starts = Object.values(response.slots ?? {})
                    .flat()
                    .map((s) => s.start)
                    .filter((s) => dayjs(s).tz(zone).format('YYYY-MM-DD') === dayKey)
                    .sort()
                setSlots(Array.from(new Set(starts)))
            })
            .catch(() => {
                if (!current) return
                setSlots([])
                setFailed(true)
            })
            .finally(() => {
                if (current) setLoading(false)
            })
        return () => {
            current = false
            request.cancel()
        }
    }, [open, dayKey, study, language, zone])

    const handleConfirm = useCallback(async () => {
        if (!selected) return
        const ok = await onConfirm(selected, reason)
        if (ok) onClose()
    }, [selected, reason, onConfirm, onClose])

    const currentSlot = formatAppointmentSlot(appointment, candidateTimezone)

    return (
        <Modal
            open={open}
            title={t`Reschedule appointment — ${candidateName}`}
            onCancel={onClose}
            onOk={handleConfirm}
            okText={t`Reschedule`}
            okButtonProps={{ disabled: !selected || isSubmitting, loading: isSubmitting }}
            destroyOnHidden
        >
            <div className='space-y-4'>
                {currentSlot && (
                    <p className='text-sm text-gray-600'>
                        {t`Currently`}: <span className='font-medium'>{currentSlot}</span>
                    </p>
                )}

                <div>
                    <label className='block text-sm font-medium mb-1' htmlFor={`reschedule-date-${candidateId}`}>
                        {t`New date`}
                    </label>
                    <DatePicker
                        id={`reschedule-date-${candidateId}`}
                        value={date}
                        onChange={(next) => {
                            setDate(next)
                            // A slot belongs to a date; keeping the selection
                            // across a date change would submit a time the
                            // coordinator is no longer looking at.
                            setSelected('')
                        }}
                        className='w-full'
                        // Past dates cannot be booked, so do not offer them.
                        disabledDate={(current) => Boolean(current && current.isBefore(dayjs().startOf('day')))}
                    />
                </div>

                {dayKey && (
                    <div>
                        <p className='text-sm font-medium mb-2'>
                            {t`Available times`} ({zone.split('/').pop()?.replace('_', ' ')})
                        </p>
                        {loading && (
                            <div data-testid='reschedule-slots-loading'>
                                <Spin size='small' /> <span className='text-sm text-gray-500'>{t`Finding available times…`}</span>
                            </div>
                        )}
                        {!loading && failed && (
                            <Alert
                                type='warning'
                                showIcon
                                message={t`Could not load available times. Please try again in a moment.`}
                                data-testid='reschedule-slots-failed'
                            />
                        )}
                        {!loading && !failed && slots.length === 0 && (
                            <p className='text-sm text-gray-600' data-testid='reschedule-slots-empty'>
                                {t`No times available on this date. Try another day.`}
                            </p>
                        )}
                        {!loading && !failed && slots.length > 0 && (
                            <div className='grid grid-cols-3 gap-2' role='group' aria-label={t`Available appointment times`}>
                                {slots.map((slot) => {
                                    const isSelected = slot === selected
                                    return (
                                        <button
                                            key={slot}
                                            type='button'
                                            aria-pressed={isSelected}
                                            onClick={() => setSelected(slot)}
                                            className={[
                                                'min-h-[40px] rounded border px-2 py-1 text-sm',
                                                isSelected
                                                    ? 'border-blue-600 bg-blue-600 text-white'
                                                    : 'border-gray-300 bg-white hover:border-blue-600'
                                            ].join(' ')}
                                        >
                                            {dayjs(slot).tz(zone).format(SLOT_TIME_FORMAT)}
                                        </button>
                                    )
                                })}
                            </div>
                        )}
                    </div>
                )}

                <div>
                    <label className='block text-sm font-medium mb-1' htmlFor={`reschedule-reason-${candidateId}`}>
                        {t`Reason (optional)`}
                    </label>
                    <Input.TextArea
                        id={`reschedule-reason-${candidateId}`}
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        rows={2}
                        maxLength={500}
                        placeholder={t`Shared with the patient in the Cal.com notification`}
                    />
                </div>
            </div>
        </Modal>
    )
}

export default RescheduleAppointmentModal
